/**
 * Delivery figures on the phone — the SAME arithmetic the server repeats in commit_delivery
 * (F4.10 "Figures computed by the server in whole grams and paise") so `phone_figures`
 * matches exactly:
 *
 *   per bag : gross = its latest non-superseded purchase weight; net = gross − sack tare
 *   per lot : gross, tare, net = sums over the lot's bags still in it (withdrawn bags excluded)
 *             payable = net × (1 − deduction)           (deduction is 0 on every card the phone uses)
 *             amount  = net × (1 − deduction) × rate    rounded half up ONCE, to whole paise
 *
 * Money is integer paise, weight integer grams; BigInt inside, never a float.
 */
import { divRoundHalfUp, type Paise } from '@/lib/money'
import type { Grams } from '@/lib/weight'
import type { PhoneFigures } from '../commands'

export interface FigureWeight {
  id: string
  gross_g: Grams
  supersedes_id: string | null
}

export interface FigureBag {
  tare_g: Grams
  weights: readonly FigureWeight[]
  withdrawn?: boolean
}

/**
 * The weight that counts for a bag: the latest one that no other weight supersedes (Fix last bag
 * and dispute re-weighs supersede; every value is kept, F4-B2.b). Null when the bag has none.
 */
export function currentWeight<W extends FigureWeight>(weights: readonly W[]): W | null {
  const superseded = new Set(weights.map((w) => w.supersedes_id).filter(Boolean))
  for (let i = weights.length - 1; i >= 0; i--) {
    const w = weights[i]!
    if (!superseded.has(w.id)) return w
  }
  return null
}

export function bagGross(bag: FigureBag): Grams {
  return currentWeight(bag.weights)?.gross_g ?? 0
}

/** Produce weight of one bag: gross − sack. */
export function bagNet(bag: FigureBag): Grams {
  return bagGross(bag) - bag.tare_g
}

/** Deduction in basis points (1% = 100). Always 0 in October: cards with rules are refused. */
export type DeductionBp = number

/** Value of the payable weight at a per-kg rate: net × (10000 − bp) × rate ÷ (10000 × 1000), half up once. */
export function amountPaise(
  netG: Grams,
  ratePaisePerKg: Paise,
  deductionBp: DeductionBp = 0,
): Paise {
  const n = BigInt(netG) * BigInt(10_000 - deductionBp) * BigInt(ratePaisePerKg)
  return Number(divRoundHalfUp(n, 10_000n * 1000n))
}

/** payable = net × (1 − deduction), whole grams (display line on the slip). */
export function payableGrams(netG: Grams, deductionBp: DeductionBp = 0): Grams {
  return Number(divRoundHalfUp(BigInt(netG) * BigInt(10_000 - deductionBp), 10_000n))
}

/** phone_figures for one lot (commit_delivery.lots[].phone_figures). */
export function lotFigures(
  bags: readonly FigureBag[],
  ratePaisePerKg: Paise,
  deductionBp: DeductionBp = 0,
): PhoneFigures {
  let gross = 0
  let tare = 0
  for (const b of bags) {
    if (b.withdrawn) continue
    gross += bagGross(b)
    tare += b.tare_g
  }
  const net = gross - tare
  return {
    gross_g: gross,
    tare_g: tare,
    net_g: net,
    payable_g: payableGrams(net, deductionBp),
    amount_paise: amountPaise(net, ratePaisePerKg, deductionBp),
  }
}

/** Money effect of a re-weigh for this delivery only (F4-B2.c "₹ before and after"). */
export function reweighEffect(
  beforeGrossG: Grams,
  afterGrossG: Grams,
  tareG: Grams,
  ratePaisePerKg: Paise,
): { before: Paise; after: Paise } {
  return {
    before: amountPaise(beforeGrossG - tareG, ratePaisePerKg),
    after: amountPaise(afterGrossG - tareG, ratePaisePerKg),
  }
}
